import React, { useState } from 'react';
import {
  Box,
  Grid,
  TextField,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Alert,
  alpha,
  Paper,
  AlertTitle
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, Lock as LockIcon } from '@mui/icons-material';
import axiosInstance from '../axiosInstance';
import { useTheme } from '@mui/material/styles';

interface PasswordAccordionProps {
  expanded: string | false;
  handleAccordionChange: (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => void;
}

const PasswordAccordion: React.FC<PasswordAccordionProps> = ({ expanded, handleAccordionChange }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const theme = useTheme();
  const isDarkTheme = theme.name === 'dark';

  const handlePasswordChange = async () => {
    if (newPassword !== newPasswordConfirm) {
      setPasswordError('New passwords do not match');
      return;
    }

    try {
      const response = await axiosInstance.put('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
        new_password_confirm: newPasswordConfirm,
      });

      if (response.status === 200) {
        setPasswordSuccess('Password updated successfully');
        setPasswordError('');
        // Clear form fields after successful password change
        setCurrentPassword('');
        setNewPassword('');
        setNewPasswordConfirm('');
      }
    } catch (error) {
      setPasswordError('Error changing password. Please try again.');
      console.error('Error changing password:', error);
    }
  };

  return (
    <Accordion
      expanded={expanded === 'panel1'}
      onChange={handleAccordionChange('panel1')}
      sx={{ 
        width: '100%', 
        maxWidth: '100%',
        backgroundColor: isDarkTheme ? alpha(theme.palette.background.paper, 0.6) : theme.palette.background.paper,
        borderRadius: '8px',
        overflow: 'hidden',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        borderLeft: expanded === 'panel1' ? '4px solid' : '1px solid',
        borderColor: expanded === 'panel1' ? 'primary.main' : 'divider',
        transition: 'all 0.3s ease'
      }}
    >
      <AccordionSummary 
        expandIcon={<ExpandMoreIcon />}
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          '&.Mui-expanded': {
            minHeight: '48px',
            bgcolor: isDarkTheme ? alpha(theme.palette.primary.main, 0.1) : alpha(theme.palette.primary.main, 0.05)
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <LockIcon sx={{ mr: 1.5, color: theme.palette.primary.main }} />
          <Typography variant="h6">Change Password</Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 3 }}>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            bgcolor: isDarkTheme ? alpha(theme.palette.background.secondary, 0.3) : alpha(theme.palette.background.secondary, 0.2),
            borderRadius: isDarkTheme ? '8px' : '0',
            mb: 2
          }}
        >
          <Typography variant="body2" color="text.secondary">
            For security purposes, please enter your current password along with your new password.
            Make sure your new password is strong and not easy to guess.
          </Typography>
        </Paper>

        <Box component="form" onSubmit={(e) => e.preventDefault()} sx={{ width: '100%' }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Current Password"
                type="password"
                fullWidth
                margin="normal"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: isDarkTheme ? '8px' : '0',
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="New Password"
                type="password"
                fullWidth
                margin="normal"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: isDarkTheme ? '8px' : '0',
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Confirm New Password"
                type="password"
                fullWidth
                margin="normal"
                value={newPasswordConfirm}
                onChange={(e) => setNewPasswordConfirm(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: isDarkTheme ? '8px' : '0',
                  }
                }}
              />
            </Grid>
            {passwordError && (
              <Grid item xs={12}>
                <Alert 
                  severity="error" 
                  sx={{ 
                    borderRadius: isDarkTheme ? '8px' : '0',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    '& .MuiAlert-icon': {
                      color: theme.palette.error.main
                    }
                  }}
                >
                  <AlertTitle>Error</AlertTitle>
                  {passwordError}
                </Alert>
              </Grid>
            )}
            {passwordSuccess && (
              <Grid item xs={12}>
                <Alert 
                  severity="success" 
                  sx={{ 
                    borderRadius: isDarkTheme ? '8px' : '0',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    '& .MuiAlert-icon': {
                      color: theme.palette.success.main
                    }
                  }}
                >
                  <AlertTitle>Success</AlertTitle>
                  {passwordSuccess}
                </Alert>
              </Grid>
            )}
            <Grid item xs={12}>
              <Button
                variant="contained"
                color="primary"
                onClick={handlePasswordChange}
                sx={{ 
                  mt: 2, 
                  borderRadius: isDarkTheme ? '4px' : '0px',
                  textTransform: 'none',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.2s ease'
                }}
                fullWidth
              >
                Update Password
              </Button>
            </Grid>
          </Grid>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default PasswordAccordion;
